import React from 'react'
import styled from '@emotion/styled'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import { Locale, Translation } from 'locale'
import { NotificationType } from 'view/shared/modal'
import { ButtonStrip, TextButton, TextButtonType } from 'view/components/buttons'
import {
  clientActionTogglePlatformFeature,
  ClientActionTogglePlatformFeatureArgs,
  CrudEventEnum,
  Group,
  Market,
  MarketStatus,
  PlatformChangeActions,
  PlatformDetail,
  PlatformFeature,
  TradingStatus,
  UserPreference
} from 'api'
import { GridId } from 'store/grid'
import { NotificationActions, ShowDialogArgs } from 'store/notification'
import { SelectionActions } from 'store/selection'
import { platformDetailSelector } from 'selectors/platformSelectors'
import * as constants from 'view/styles/constants'
import { PlatformToggle } from './PlatformToggle'
import { canTriggerSupportActionsSelector } from '../../../../selectors/permissionSelectors'
import { userPreferenceSelector } from 'selectors/userPreferenceSelectors'
import { createUserHasGroupSelector } from 'selectors/userSelectors'

export interface PlatformSettingsProps {
  platformDetail: PlatformDetail | null
  canTriggerSupportActions: boolean
  userPreference: UserPreference
  hasUserAdaptiveAdminGroup: boolean
  onShowDialog: (dialogArgs: ShowDialogArgs) => unknown
  setCurrentGridId: (gridId: GridId | null) => unknown
  togglePlatformFeature: (args: ClientActionTogglePlatformFeatureArgs) => void
}

const mapStateToProps = createStructuredSelector({
  userPreference: userPreferenceSelector,
  hasUserAdaptiveAdminGroup: createUserHasGroupSelector(Group.GROUP_ADAPTIVE_ADMIN),
  platformDetail: platformDetailSelector,
  canTriggerSupportActions: canTriggerSupportActionsSelector
})

const mapDispatchToProps = {
  onShowDialog: NotificationActions.showDialog,
  setCurrentGridId: SelectionActions.setCurrentGridId,
  togglePlatformFeature: clientActionTogglePlatformFeature
}

const Row = styled.div`
  display: flex;
  margin: 20px;
`

const Column = styled.div`
  &:not(:last-child) {
    padding-right: 20px;
    margin-right: 20px;
    border-right: 2px solid ${constants.CORE_PRIMARY_4};
  }

  & > *:not(:last-child) {
    margin-bottom: 20px;
  }
`

const Header = styled.div`
  font-size: 18px;
  font-family: NEXWeb;
  font-weight: ${constants.FontWeight.Medium};
  color: ${constants.CORE_PRIMARY_6};
`

export const StatusIndicator = styled.span<{ color: string }>`
  margin-left: 12px;
  margin-right: 6px;
  color: ${props => props.color};
  text-transform: uppercase;
  font-weight: ${constants.FontWeight.Bold};
  font-size: 14px;

  &:not(:first-child) {
    margin-left: 6px;
  }
`

const Narrative = styled.span`
  color: ${constants.ACCENT_BAD};
  font-family: NEXWeb;
  font-size: 16px;
  font-weight: ${constants.FontWeight.Medium};
`

const TextInfo = styled.p`
  font-family: NEXWeb;
  font-size: 16px;
  font-weight: ${constants.FontWeight.Medium};
`

const Container = styled.div``

export const BigButton = styled(TextButton)`
  font-size: 14px;
  height: 54px;
  width: 160px;
`

export const FeatureToggleTable = styled.table`
  width: 300px;
  margin: 12px 0 0 0;
  text-align: left;
  border-spacing: 0;

  th,
  td {
    padding: 5px 0;
    font-size: 12px;
  }
`

export class PlatformSettingsComponent extends React.Component<PlatformSettingsProps> {
  componentDidMount() {
    this.props.setCurrentGridId(null)
  }

  render() {
    const { hasUserAdaptiveAdminGroup, platformDetail, userPreference } = this.props

    const tradeDateEUFormatted = Locale.formatDate(
      platformDetail?.platform.marketDetails[Market.EUROPE]?.tradeDate ?? '',
      userPreference.dateFormat
    )
    const tradeDateUSFormatted = Locale.formatDate(
      platformDetail?.platform.marketDetails[Market.US]?.tradeDate ?? '',
      userPreference.dateFormat
    )

    const tradingStatusEU = platformDetail?.platform.marketDetails[Market.EUROPE]?.tradingStatus
    const marketStatusEU = platformDetail?.platform.marketDetails[Market.EUROPE]?.marketStatus
    const tradingStatusUS = platformDetail?.platform.marketDetails[Market.US]?.tradingStatus
    const marketStatusUS = platformDetail?.platform.marketDetails[Market.US]?.marketStatus

    const isPlatformSuspendedEU = tradingStatusEU !== TradingStatus.ENABLED
    const isMarketClosedEU = marketStatusEU === MarketStatus.MARKET_CLOSED
    const isMarketOpenEU = marketStatusEU === MarketStatus.MARKET_OPEN
    const isPlatformSuspendedUS = tradingStatusUS !== TradingStatus.ENABLED
    const isMarketClosedUS = marketStatusUS === MarketStatus.MARKET_CLOSED
    const isMarketPreOpenUS = marketStatusUS === MarketStatus.MARKET_PRE_OPEN
    const isMarketOpenUS = marketStatusUS === MarketStatus.MARKET_OPEN

    const canUpdatePlatform = platformDetail && platformDetail.availableEvents.includes(CrudEventEnum.UPSERT)

    const getTextWithNextStatus = (translation: Translation) => (newStatus: boolean) => {
      return Locale.translate(translation, { enabled: newStatus })
    }

    return (
      <Container>
        <Row>
          <Column>
            <Header>
              {Locale.translate(Translation.TC_EU_MARKET)}
              <StatusIndicator color={isPlatformSuspendedEU ? constants.ACCENT_BAD : constants.ACCENT_GOOD}>
                {Locale.translate(isPlatformSuspendedEU ? Translation.TC_SUSPENDED : Translation.TC_ACTIVE)}
              </StatusIndicator>
              /
              <StatusIndicator
                color={isMarketClosedEU ? constants.ACCENT_BAD : isMarketOpenEU ? constants.ACCENT_GOOD : constants.ACCENT_BE_AWARE}>
                {Locale.translate(
                  isMarketClosedEU ? Translation.TC_CLOSED : isMarketOpenEU ? Translation.TC_OPEN : Translation.TC_PRE_OPEN
                )}
              </StatusIndicator>
            </Header>
            <TextInfo>
              {Locale.translate(Translation.TC_MARKET_TRADE_DATE_INFO, {
                tradeDate: tradeDateEUFormatted
              })}
            </TextInfo>
            <ButtonStrip spacing="7px">
              <BigButton
                dataTestId={'PlatformSettings-BigButton-ActionActivateTrading'}
                type={TextButtonType.Good}
                disabled={!isPlatformSuspendedEU || !canUpdatePlatform}
                onClick={this.handleActivatePlatformEurope}>
                {Locale.translate(Translation.TC_ACTION_ACTIVATE_TRADING_EU)}
              </BigButton>
              <BigButton
                dataTestId={'PlatformSettings-BigButton-ActionSuspendTrading'}
                type={TextButtonType.Bad}
                disabled={isPlatformSuspendedEU || !canUpdatePlatform}
                onClick={this.handleSuspendPlatformEurope}>
                {Locale.translate(Translation.TC_ACTION_SUSPEND_TRADING_EU)}
              </BigButton>
            </ButtonStrip>
            <ButtonStrip spacing="7px">
              <BigButton
                dataTestId={'PlatformSettings-BigButton-ActionStartDay'}
                type={TextButtonType.Good}
                disabled={isMarketOpenEU || !canUpdatePlatform}
                onClick={this.handleStartDayEurope}>
                {Locale.translate(Translation.TC_ACTION_START_DAY_EU)}
              </BigButton>
              {hasUserAdaptiveAdminGroup && (
                <BigButton
                  dataTestId={'PlatformSettings-BigButton-ActionStartDayTomorrow'}
                  type={TextButtonType.Good}
                  disabled={isMarketOpenEU || !canUpdatePlatform}
                  onClick={this.handleStartDayEuropeTomorrow}>
                  {Locale.translate(Translation.TC_ACTION_START_DAY_EU_TOMORROW)}
                </BigButton>
              )}
              <BigButton
                dataTestId={'PlatformSettings-BigButton-ActionEndDay'}
                type={TextButtonType.Bad}
                disabled={!isMarketOpenEU || !canUpdatePlatform}
                onClick={this.handleEndDayEurope}>
                {Locale.translate(Translation.TC_ACTION_END_DAY_EU)}
              </BigButton>
            </ButtonStrip>
          </Column>
          <Column>
            <Header>
              {Locale.translate(Translation.TC_US_MARKET)}
              <StatusIndicator color={isPlatformSuspendedUS ? constants.ACCENT_BAD : constants.ACCENT_GOOD}>
                {Locale.translate(isPlatformSuspendedUS ? Translation.TC_SUSPENDED : Translation.TC_ACTIVE)}
              </StatusIndicator>
              /
              <StatusIndicator
                color={isMarketClosedUS ? constants.ACCENT_BAD : isMarketOpenUS ? constants.ACCENT_GOOD : constants.ACCENT_BE_AWARE}>
                {Locale.translate(
                  isMarketClosedUS ? Translation.TC_CLOSED : isMarketOpenUS ? Translation.TC_OPEN : Translation.TC_PRE_OPEN
                )}
              </StatusIndicator>
            </Header>
            <TextInfo>
              {Locale.translate(Translation.TC_MARKET_TRADE_DATE_INFO, {
                tradeDate: tradeDateUSFormatted
              })}
            </TextInfo>
            <ButtonStrip spacing="7px">
              <BigButton
                dataTestId={'PlatformSettings-BigButton-ActionActivateTradingUS'}
                type={TextButtonType.Good}
                disabled={!isPlatformSuspendedUS || !canUpdatePlatform}
                onClick={this.handleActivatePlatformUS}>
                {Locale.translate(Translation.TC_ACTION_ACTIVATE_TRADING_US)}
              </BigButton>
              <BigButton
                dataTestId={'PlatformSettings-BigButton-ActionSuspendTradingUS'}
                type={TextButtonType.Bad}
                disabled={isPlatformSuspendedUS || !canUpdatePlatform}
                onClick={this.handleSuspendPlatformUS}>
                {Locale.translate(Translation.TC_ACTION_SUSPEND_TRADING_US)}
              </BigButton>
            </ButtonStrip>
            <ButtonStrip spacing="7px">
              <BigButton
                dataTestId={'PlatformSettings-BigButton-ActionPreStartDayUS'}
                type={TextButtonType.Warn}
                disabled={!isMarketClosedUS || !canUpdatePlatform}
                onClick={this.handlePreStartDayUS}>
                {Locale.translate(Translation.TC_ACTION_PRE_START_DAY_US)}
              </BigButton>
              <BigButton
                dataTestId={'PlatformSettings-BigButton-ActionStartDayUS'}
                type={TextButtonType.Good}
                disabled={!isMarketPreOpenUS || !canUpdatePlatform}
                onClick={this.handleStartDayUS}>
                {Locale.translate(Translation.TC_ACTION_START_DAY_US)}
              </BigButton>
              <BigButton
                dataTestId={'PlatformSettings-BigButton-ActionEndDayUS'}
                type={TextButtonType.Bad}
                disabled={!isMarketOpenUS || !canUpdatePlatform}
                onClick={this.handleEndDayUS}>
                {Locale.translate(Translation.TC_ACTION_END_DAY_US)}
              </BigButton>
            </ButtonStrip>
          </Column>
        </Row>
        <Row>
          <Narrative>{Locale.translate(Translation.SC_NARRATIVE_PLATFORM_WARNING)}</Narrative>
        </Row>
        <Row>
          <Header>
            {Locale.translate(Translation.TC_PLATFORM_FEATURE_TOGGLES)}
            <FeatureToggleTable>
              <thead>
                <tr>
                  <th>{Locale.translate(Translation.TC_PLATFORM_FEATURE)}</th>
                  <th>{Locale.translate(Translation.TC_ENABLED)}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{Locale.translate(Translation.TC_ALLOW_PARTIAL_FILLS)}</td>
                  <td id="allowPartialFeature">
                    <PlatformToggle
                      aria-labelledby={'allowPartialFeature'}
                      getConfirmationTitle={getTextWithNextStatus(Translation.TC_PROMPT_TITLE_TOGGLE_ALLOW_PARTIAL)}
                      getConfirmationMessage={getTextWithNextStatus(Translation.SC_PROMPT_MSG_TOGGLE_ALLOW_PARTIAL)}
                      platformFeature={PlatformFeature.ALLOW_PARTIAL}
                      readOnly={!isMarketClosedEU || !isMarketClosedUS || !canUpdatePlatform}
                    />
                  </td>
                </tr>
                <tr>
                  <td id="fixTradeQueriesFeature">{Locale.translate(Translation.TC_FIX_TRADE_QUERIES)}</td>
                  <td>
                    <PlatformToggle
                      aria-labelledby={'fixTradeQueriesFeature'}
                      getConfirmationTitle={getTextWithNextStatus(Translation.TC_PROMPT_TITLE_TOGGLE_FIX_TRADE_QUERIES)}
                      getConfirmationMessage={getTextWithNextStatus(Translation.SC_PROMPT_MSG_TOGGLE_FIX_TRADE_QUERIES)}
                      platformFeature={PlatformFeature.FIX_TRADE_QUERIES}
                      readOnly={!canUpdatePlatform}
                    />
                  </td>
                </tr>
                <tr>
                  <td id="curveCollectionsFeature">{Locale.translate(Translation.TC_ENABLE_CURVE_COLLECTIONS)}</td>
                  <td>
                    <PlatformToggle
                      aria-labelledby={'curveCollectionsFeature'}
                      data-test-id="FeatureToggle-CurveCollections"
                      getConfirmationTitle={getTextWithNextStatus(Translation.TC_PROMPT_TITLE_TOGGLE_ENABLE_CURVE_COLLECTIONS)}
                      getConfirmationMessage={getTextWithNextStatus(Translation.SC_PROMPT_MSG_TOGGLE_ENABLE_CURVE_COLLECTIONS)}
                      platformFeature={PlatformFeature.ENABLE_CURVE_COLLECTIONS}
                      readOnly={!canUpdatePlatform}
                    />
                  </td>
                </tr>
                <tr>
                  <td>{Locale.translate(Translation.TC_ENABLE_START_CONSIDERATION_OVERRIDE)}</td>
                  <td id="startConsiderationFeature">
                    <PlatformToggle
                      aria-labelledby={'startConsiderationFeature'}
                      getConfirmationTitle={getTextWithNextStatus(Translation.TC_PROMPT_TITLE_TOGGLE_ENABLE_START_CONSIDERATION_OVERRIDE)}
                      getConfirmationMessage={getTextWithNextStatus(Translation.SC_PROMPT_MSG_TOGGLE_ENABLE_START_CONSIDERATION_OVERRIDE)}
                      platformFeature={PlatformFeature.ENABLE_START_CONSIDERATION_OVERRIDE}
                      readOnly={!isMarketClosedEU || !isMarketClosedUS || !canUpdatePlatform}
                    />
                  </td>
                </tr>
                <tr>
                  <td id="corporateFeatures">{Locale.translate(Translation.TC_ENABLE_CORPORATES)}</td>
                  <td>
                    <PlatformToggle
                      aria-labelledby={'corporateFeatures'}
                      getConfirmationTitle={getTextWithNextStatus(Translation.TC_PROMPT_TITLE_TOGGLE_CORPORATES)}
                      getConfirmationMessage={getTextWithNextStatus(Translation.SC_PROMPT_MSG_TOGGLE_CORPORATES)}
                      platformFeature={PlatformFeature.ENABLE_CORPORATES}
                      readOnly={!canUpdatePlatform}
                    />
                  </td>
                </tr>
                <tr>
                  <td>{Locale.translate(Translation.TC_ENABLE_MBS)}</td>
                  <td id="mbsFeature">
                    <PlatformToggle
                      aria-labelledby={'mbsFeature'}
                      getConfirmationTitle={getTextWithNextStatus(Translation.TC_PROMPT_TITLE_TOGGLE_MBS)}
                      getConfirmationMessage={getTextWithNextStatus(Translation.SC_PROMPT_MSG_TOGGLE_MBS)}
                      platformFeature={PlatformFeature.ENABLE_MBS}
                      readOnly={!canUpdatePlatform}
                    />
                  </td>
                </tr>
                <tr>
                  <td id="evergreenFeature">{Locale.translate(Translation.TC_ENABLE_EVERGREENS)}</td>
                  <td>
                    <PlatformToggle
                      aria-labelledby={'evergreenFeature'}
                      getConfirmationMessage={getTextWithNextStatus(Translation.SC_PROMPT_MSG_TOGGLE_EVERGREENS)}
                      getConfirmationTitle={getTextWithNextStatus(Translation.TC_PROMPT_TITLE_TOGGLE_EVERGREENS)}
                      platformFeature={PlatformFeature.ENABLE_EVERGREENS}
                      readOnly={!isMarketClosedEU || !isMarketClosedUS || !canUpdatePlatform}
                    />
                  </td>
                </tr>
                <>
                  {this.props.canTriggerSupportActions && (
                    <tr>
                      <td id="clustBatchingFeature">{Locale.translate(Translation.TC_ENABLE_BATCHING)}</td>
                      <td>
                        <PlatformToggle
                          aria-labelledby={'clustBatchingFeature'}
                          getConfirmationMessage={getTextWithNextStatus(Translation.SC_PROMPT_MSG_TOGGLE_CLUSTER_BATCHING)}
                          getConfirmationTitle={getTextWithNextStatus(Translation.TC_PROMPT_TITLE_TOGGLE_CLUSTER_BATCHING)}
                          platformFeature={PlatformFeature.ENABLE_CLUSTER_BATCHING}
                          readOnly={!canUpdatePlatform}
                        />
                      </td>
                    </tr>
                  )}
                </>
                <>
                  {this.props.canTriggerSupportActions && (
                    <tr>
                      <td id="lightWeightPriceFeature">{Locale.translate(Translation.TC_ENABLE_LIGHT_WEIGHT_PRICE)}</td>
                      <td>
                        <PlatformToggle
                          aria-labelledby={'lightWeightPriceFeature'}
                          getConfirmationMessage={getTextWithNextStatus(Translation.SC_PROMPT_MSG_TOGGLE_LIGHT_WEIGHT_PRICE)}
                          getConfirmationTitle={getTextWithNextStatus(Translation.TC_PROMPT_TITLE_TOGGLE_LIGHT_WEIGHT_PRICE)}
                          platformFeature={PlatformFeature.ENABLE_LIGHT_WEIGHT_PRICE}
                          readOnly={!canUpdatePlatform}
                        />
                      </td>
                    </tr>
                  )}
                </>
                <>
                  {this.props.canTriggerSupportActions && (
                    <tr>
                      <td id="lightWeightPriceWebFeature">{Locale.translate(Translation.TC_ENABLE_LIGHT_WEIGHT_PRICE_WEB)}</td>
                      <td>
                        <PlatformToggle
                          aria-labelledby={'lightWeightPriceWebFeature'}
                          getConfirmationMessage={getTextWithNextStatus(Translation.SC_PROMPT_MSG_TOGGLE_LIGHT_WEIGHT_PRICE_WEB)}
                          getConfirmationTitle={getTextWithNextStatus(Translation.TC_PROMPT_TITLE_TOGGLE_LIGHT_WEIGHT_PRICE_WEB)}
                          platformFeature={PlatformFeature.ENABLE_LIGHT_WEIGHT_PRICE_WEB}
                          readOnly={!canUpdatePlatform}
                        />
                      </td>
                    </tr>
                  )}
                </>
                <>
                  {this.props.canTriggerSupportActions && (
                    <tr>
                      <td id="firdsFeature">{Locale.translate(Translation.TC_ENABLE_FIRDS)}</td>
                      <td>
                        <PlatformToggle
                          aria-labelledby={'firdsFeature'}
                          getConfirmationMessage={getTextWithNextStatus(Translation.SC_PROMPT_MSG_TOGGLE_FIRDS)}
                          getConfirmationTitle={getTextWithNextStatus(Translation.TC_PROMPT_TITLE_TOGGLE_FIRDS)}
                          platformFeature={PlatformFeature.ENABLE_FIRDS}
                          readOnly={!canUpdatePlatform}
                        />
                      </td>
                    </tr>
                  )}
                </>
                <tr>
                  <td id="unknownCollateralFeature">{Locale.translate(Translation.TC_ENABLE_SPREADSHEET_UNKNOWN_COLLATERAL)}</td>
                  <td>
                    <PlatformToggle
                      aria-labelledby={'unknownCollateralFeature'}
                      getConfirmationMessage={getTextWithNextStatus(Translation.SC_PROMPT_MSG_TOGGLE_SPREADSHEET_UNKNOWN_COLLATERAL)}
                      getConfirmationTitle={getTextWithNextStatus(Translation.TC_PROMPT_TITLE_TOGGLE_SPREADSHEET_UNKNOWN_COLLATERAL)}
                      platformFeature={PlatformFeature.ENABLE_SPREADSHEET_UNKNOWN_COLLATERAL}
                      readOnly={!canUpdatePlatform}
                    />
                  </td>
                </tr>
                <tr>
                  <td id="realTimePurgeFeature">{Locale.translate(Translation.TC_ENABLE_REAL_TIME_PURGE)}</td>
                  <td>
                    <PlatformToggle
                      aria-labelledby={'unknownCollateralFeature'}
                      getConfirmationMessage={getTextWithNextStatus(Translation.SC_PROMPT_MSG_TOGGLE_REAL_TIME_PURGE)}
                      getConfirmationTitle={getTextWithNextStatus(Translation.TC_PROMPT_TITLE_TOGGLE_REAL_TIME_PURGE)}
                      platformFeature={PlatformFeature.ENABLE_REAL_TIME_PURGE}
                      readOnly={!canUpdatePlatform}
                    />
                  </td>
                </tr>
                <tr>
                  <td id="negotiationInFlightFeature">{Locale.translate(Translation.TC_ENABLE_NEGOTIATION_INFLIGHT)}</td>
                  <td>
                    <PlatformToggle
                      aria-labelledby={'unknownCollateralFeature'}
                      getConfirmationMessage={getTextWithNextStatus(Translation.SC_PROMPT_MSG_TOGGLE_NEGOTIATION_INFLIGHT)}
                      getConfirmationTitle={getTextWithNextStatus(Translation.TC_PROMPT_TITLE_TOGGLE_NEGOTIATION_INFLIGHT)}
                      platformFeature={PlatformFeature.ENABLE_NEGOTIATION_INFLIGHT}
                      readOnly={!canUpdatePlatform}
                    />
                  </td>
                </tr>
              </tbody>
            </FeatureToggleTable>
          </Header>
        </Row>
      </Container>
    )
  }

  private handleSuspendPlatformEurope = () => {
    const { platformDetail } = this.props

    if (!platformDetail) {
      throw new Error('Unable to locate current platform settings')
    }

    this.props.onShowDialog({
      type: NotificationType.Confirm,
      titleText: Locale.translate(Translation.TC_PROMPT_TITLE_SUSPEND_PLATFORM_EU),
      messageText: Locale.translate(Translation.SC_PROMPT_MSG_SUSPEND_PLATFORM_EU),
      buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
      defaultButton: Translation.TC_BUTTON_YES,
      cancelButton: Translation.TC_BUTTON_NO,
      responseMap: {
        [Translation.TC_BUTTON_YES]: [PlatformChangeActions.suspendTradingEurope]
      }
    })
  }

  private handleActivatePlatformEurope = () => {
    const { platformDetail } = this.props

    if (!platformDetail) {
      throw new Error('Unable to locate current platform settings')
    }

    this.props.onShowDialog({
      type: NotificationType.Confirm,
      titleText: Locale.translate(Translation.TC_PROMPT_TITLE_ACTIVATE_PLATFORM_EU),
      messageText: Locale.translate(Translation.SC_PROMPT_MSG_ACTIVATE_PLATFORM_EU),
      buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
      defaultButton: Translation.TC_BUTTON_YES,
      cancelButton: Translation.TC_BUTTON_NO,
      responseMap: {
        [Translation.TC_BUTTON_YES]: [PlatformChangeActions.activateTradingEurope]
      }
    })
  }

  private handleStartDayEurope = async () => {
    const { platformDetail } = this.props

    if (!platformDetail) {
      throw new Error('Unable to locate current platform settings')
    }

    this.props.onShowDialog({
      type: NotificationType.Confirm,
      titleText: Locale.translate(Translation.TC_PROMPT_TITLE_START_DAY_EU),
      messageText: Locale.translate(Translation.SC_PROMPT_MSG_START_DAY_EU),
      buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
      defaultButton: Translation.TC_BUTTON_YES,
      cancelButton: Translation.TC_BUTTON_NO,
      responseMap: {
        [Translation.TC_BUTTON_YES]: [PlatformChangeActions.startOfDayEurope]
      }
    })
  }

  private handleStartDayEuropeTomorrow = async () => {
    const { platformDetail } = this.props

    if (!platformDetail) {
      throw new Error('Unable to locate current platform settings')
    }

    this.props.onShowDialog({
      type: NotificationType.Confirm,
      titleText: Locale.translate(Translation.TC_PROMPT_TITLE_START_DAY_EU_TOMORROW),
      messageText: Locale.translate(Translation.SC_PROMPT_MSG_START_DAY_EU_TOMORROW),
      buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
      defaultButton: Translation.TC_BUTTON_YES,
      cancelButton: Translation.TC_BUTTON_NO,
      responseMap: {
        [Translation.TC_BUTTON_YES]: [PlatformChangeActions.startOfDayEuropeTomorrow]
      }
    })
  }

  private handleEndDayEurope = () => {
    const { platformDetail } = this.props

    if (!platformDetail) {
      throw new Error('Unable to locate current platform settings')
    }

    this.props.onShowDialog({
      type: NotificationType.Confirm,
      titleText: Locale.translate(Translation.TC_PROMPT_TITLE_END_DAY_EU),
      messageText: Locale.translate(Translation.SC_PROMPT_MSG_END_DAY_EU),
      buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
      defaultButton: Translation.TC_BUTTON_YES,
      cancelButton: Translation.TC_BUTTON_NO,
      responseMap: {
        [Translation.TC_BUTTON_YES]: [PlatformChangeActions.endOfDayEurope]
      }
    })
  }

  private handleSuspendPlatformUS = () => {
    const { platformDetail } = this.props

    if (!platformDetail) {
      throw new Error('Unable to locate current platform settings')
    }

    this.props.onShowDialog({
      type: NotificationType.Confirm,
      titleText: Locale.translate(Translation.TC_PROMPT_TITLE_SUSPEND_PLATFORM_US),
      messageText: Locale.translate(Translation.SC_PROMPT_MSG_SUSPEND_PLATFORM_US),
      buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
      defaultButton: Translation.TC_BUTTON_YES,
      cancelButton: Translation.TC_BUTTON_NO,
      responseMap: {
        [Translation.TC_BUTTON_YES]: [PlatformChangeActions.suspendTradingUS]
      }
    })
  }

  private handleActivatePlatformUS = () => {
    const { platformDetail } = this.props

    if (!platformDetail) {
      throw new Error('Unable to locate current platform settings')
    }

    this.props.onShowDialog({
      type: NotificationType.Confirm,
      titleText: Locale.translate(Translation.TC_PROMPT_TITLE_ACTIVATE_PLATFORM_US),
      messageText: Locale.translate(Translation.SC_PROMPT_MSG_ACTIVATE_PLATFORM_US),
      buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
      defaultButton: Translation.TC_BUTTON_YES,
      cancelButton: Translation.TC_BUTTON_NO,
      responseMap: {
        [Translation.TC_BUTTON_YES]: [PlatformChangeActions.activateTradingUS]
      }
    })
  }

  private handlePreStartDayUS = async () => {
    const { platformDetail } = this.props

    if (!platformDetail) {
      throw new Error('Unable to locate current platform settings')
    }

    this.props.onShowDialog({
      type: NotificationType.Confirm,
      titleText: Locale.translate(Translation.TC_PROMPT_TITLE_PRE_START_DAY_US),
      messageText: Locale.translate(Translation.SC_PROMPT_MSG_PRE_START_DAY_US),
      buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
      defaultButton: Translation.TC_BUTTON_YES,
      cancelButton: Translation.TC_BUTTON_NO,
      responseMap: {
        [Translation.TC_BUTTON_YES]: [PlatformChangeActions.preStartOfDayUS]
      }
    })
  }

  private handleStartDayUS = async () => {
    const { platformDetail } = this.props

    if (!platformDetail) {
      throw new Error('Unable to locate current platform settings')
    }

    this.props.onShowDialog({
      type: NotificationType.Confirm,
      titleText: Locale.translate(Translation.TC_PROMPT_TITLE_START_DAY_US),
      messageText: Locale.translate(Translation.SC_PROMPT_MSG_START_DAY_US),
      buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
      defaultButton: Translation.TC_BUTTON_YES,
      cancelButton: Translation.TC_BUTTON_NO,
      responseMap: {
        [Translation.TC_BUTTON_YES]: [PlatformChangeActions.startOfDayUS]
      }
    })
  }

  private handleEndDayUS = () => {
    const { platformDetail } = this.props

    if (!platformDetail) {
      throw new Error('Unable to locate current platform settings')
    }

    this.props.onShowDialog({
      type: NotificationType.Confirm,
      titleText: Locale.translate(Translation.TC_PROMPT_TITLE_END_DAY_US),
      messageText: Locale.translate(Translation.SC_PROMPT_MSG_END_DAY_US),
      buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
      defaultButton: Translation.TC_BUTTON_YES,
      cancelButton: Translation.TC_BUTTON_NO,
      responseMap: {
        [Translation.TC_BUTTON_YES]: [PlatformChangeActions.endOfDayUS]
      }
    })
  }
}

export const PlatformSettings = connect(mapStateToProps, mapDispatchToProps)(PlatformSettingsComponent)
